import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Template } from '@workspaces/models/templates';
import * as pdfjsLib from 'pdfjs-dist';
import { WorkspaceTemplateService } from '@workspaces/services/templates.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-template-preview-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './template-preview-dialog.component.html',
  styleUrls: ['./template-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplatePreviewDialogComponent implements OnInit, OnDestroy {
  @ViewChild('pdfCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  public isLoading = true;
  public error: string | null = null;
  private pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
  public currentPage = 1;
  public totalPages = 0;
  private readonly scale: number = 1; // Controls zoom level
  constructor(
    public dialogRef: MatDialogRef<TemplatePreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { template: Template },
    private readonly workspaceTemplateService: WorkspaceTemplateService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef
  ) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.mjs';
  }

  ngOnInit(): void {
    this.loadPdf();
  }
  private async loadPdf(): Promise<void> {
    if (!this.data.template.s3_key) {
      return;
    }

    const extension = this.data.template.s3_key.split('.').pop()?.toLowerCase();
    if (extension !== 'pdf') {
      this.error = 'Unable display the file. It is not a pdf.';
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;

      const downloadUrl = await new Promise<string>((resolve, reject) => {
        this.workspaceTemplateService.generateDownloadUrl(this.data.template.id, this.data.template.version).subscribe({
          next: res => resolve(res.url),
          error: err => reject(err)
        });
      });

      this.pdfDocument = await pdfjsLib.getDocument(downloadUrl).promise;
      this.totalPages = this.pdfDocument.numPages;
      this.cdr.markForCheck();
      this.renderPage(this.currentPage);
    } catch {
      this.error = 'Failed to load the PDF document.';
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async renderPage(pageNumber: number): Promise<void> {
    if (!this.pdfDocument) return;

    this.isLoading = true;

    try {
      const page = await this.pdfDocument.getPage(pageNumber);
      const viewport = page.getViewport({ scale: this.scale });
      const canvas = this.canvasRef.nativeElement;
      const context = canvas.getContext('2d');

      if (!context) {
        return;
      }

      // Set canvas dimensions
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      await page.render(renderContext).promise;
      this.currentPage = pageNumber;
      console.log('Page rendered successfully:', pageNumber);
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Error rendering page:', err);
      this.error = 'Failed to render page ' + pageNumber;
      this.cdr.markForCheck();
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages && !this.isLoading) {
      this.renderPage(this.currentPage + 1);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1 && !this.isLoading) {
      this.renderPage(this.currentPage - 1);
    }
  }

  downloadFile() {
    if (!this.data.template.s3_key) {
      return;
    }

    const extension = this.data.template.s3_key.split('.').pop()?.toLowerCase();

    this.workspaceTemplateService
      .generateDownloadUrl(this.data.template.id, this.data.template.version)
      .subscribe(res => {
        fetch(res.url)
          .then(resp => (resp.status === 200 ? resp.blob() : Promise.reject('something went wrong')))
          .then(blob => {
            this.triggerDownload(blob, `${this.data.template.name}.${extension}`);
          });
      });
  }

  private triggerDownload(blob: Blob, filename: string) {
    try {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      // Can be a race condiditon, so wait a bit
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch {
      this.snackBar.open('Failed to download file', 'Dismiss', { duration: 3000 });
    }
  }

  close() {
    this.dialogRef.close();
  }
  ngOnDestroy(): void {
    // Clean up the PDF document when the dialog is closed
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
  }
}
